import { createResourceLocatorString } from './routes';

/**
 * Console option values (plural IDs) → singular labels for cards/profiles.
 * Filters keep Console option names: Creators, Performers, Athletes and Fitness.
 */
const SELLER_CATEGORY_SINGULAR_BY_OPTION = {
  creators: 'Creator',
  performers: 'Performer',
  athletes_and_fitness: 'Athlete & Fitness',
};

/** Fallback if enum lookup only has the plural option name. */
const SELLER_CATEGORY_SINGULAR_BY_LABEL = {
  Creators: 'Creator',
  Performers: 'Performer',
  'Athletes and Fitness': 'Athlete & Fitness',
  'Athletes & Fitness': 'Athlete & Fitness',
};

/**
 * Convert a plural Console label (or option id) to the singular display form.
 * @param {string} optionId
 * @param {string} label
 * @returns {string}
 */
export const toSingularSellerCategoryLabel = (optionId, label) => {
  const normalizedOption = `${optionId || ''}`.trim().toLowerCase();
  if (normalizedOption && SELLER_CATEGORY_SINGULAR_BY_OPTION[normalizedOption]) {
    return SELLER_CATEGORY_SINGULAR_BY_OPTION[normalizedOption];
  }

  const trimmedLabel = `${label || ''}`.trim();
  if (trimmedLabel && SELLER_CATEGORY_SINGULAR_BY_LABEL[trimmedLabel]) {
    return SELLER_CATEGORY_SINGULAR_BY_LABEL[trimmedLabel];
  }

  return trimmedLabel || optionId || '';
};

/**
 * Singular seller-category label for cards/profiles (filters keep Console plurals).
 * @param {Object} publicData
 * @param {Array} [userFieldConfig]
 * @returns {string}
 */
export const getSellerCategoryLabel = (publicData, userFieldConfig = []) => {
  const sellerCategoryValue =
    typeof publicData?.seller_category === 'string' ? publicData.seller_category.trim() : '';
  const sellerCategoryConfig = userFieldConfig.find(field => field?.key === 'seller_category');
  const sellerCategoryOption = sellerCategoryConfig?.enumOptions?.find(
    option => option.option === sellerCategoryValue
  );
  const pluralLabel = sellerCategoryOption?.label || sellerCategoryValue;

  return toSingularSellerCategoryLabel(sellerCategoryValue, pluralLabel);
};

/**
 * Plural enum options for search/filter selectors (as configured in Console).
 * @param {Array} [userFields]
 * @returns {Array}
 */
export const getSellerCategoryOptions = userFields => {
  const sellerCategoryConfig = userFields?.find(field => field?.key === 'seller_category');
  return sellerCategoryConfig?.enumOptions || [];
};

export const createSellerCardData = (seller, userFieldConfig, routeConfiguration) => {
  const publicData = seller?.attributes?.profile?.publicData || {};
  const displayName = seller?.attributes?.profile?.displayName;
  const sellerId = seller?.id?.uuid;

  if (!sellerId || !displayName) {
    return null;
  }

  return {
    id: sellerId,
    category: getSellerCategoryLabel(publicData, userFieldConfig),
    name: displayName,
    subtitle: null,
    itemsCountText: null,
    profileImage: seller.profileImage,
    profileHref: createResourceLocatorString(
      'ProfilePage',
      routeConfiguration,
      { id: sellerId },
      {}
    ),
  };
};
